import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiEdit2 } from "react-icons/fi";
import { WaterQualityCard } from "../../components/Cards";
import { IotmateButton, DialogBox } from "../../components/Shared";
import { DataHistoryTable } from "../../components/Table";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { treatedWaterParameters, generateDummyTreatedWaterDetails, generateDummyTreatedWaterHistory } from "../../Data/Data";
import "./TreatedWaterDetails.css";

const TreatedWaterDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [historicalData, setHistoricalData] = useState([]);

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        // Only show loading overlay on initial load
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`treated_water_details/${deviceId}`);

        setDeviceData(data || generateDummyTreatedWaterDetails(deviceId));
      } catch (error) {
        console.error("Error fetching treated water details:", error);

        // Set dummy data on error
        setDeviceData(generateDummyTreatedWaterDetails(deviceId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // Fetch historical data on component mount
  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        const data = await fetchDataFromApi(`treated_water_history/${deviceId}`);
        setHistoricalData(data || generateDummyTreatedWaterHistory(deviceId));
      } catch (error) {
        console.error("Error fetching historical data:", error);
        setHistoricalData(generateDummyTreatedWaterHistory(deviceId));
      }
    };

    fetchHistoricalData();
  }, [deviceId]);

  // Define columns for the historical data table
  const historyColumns = useMemo(
    () => [
      {
        Header: "Timestamp",
        accessor: "timestamp",
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
      {
        Header: "pH Level",
        accessor: "ph",
      },
      {
        Header: "TDS (ppm)",
        accessor: "tds",
      },
      {
        Header: "Turbidity (NTU)",
        accessor: "turbidity",
      },
      {
        Header: "Temperature (°C)",
        accessor: "temperature",
      },
      {
        Header: "DO (mg/L)",
        accessor: "dissolvedOxygen",
      },
      {
        Header: "Chlorine (mg/L)",
        accessor: "chlorine",
      },
      {
        Header: "BOD (mg/L)",
        accessor: "bod",
      },
      {
        Header: "COD (mg/L)",
        accessor: "cod",
      },
      {
        Header: "Ammonia (mg/L)",
        accessor: "ammonia",
      },
      {
        Header: "Nitrate (mg/L)",
        accessor: "nitrate",
      },
      {
        Header: "Flow Rate (L/min)",
        accessor: "flowRate",
      },
    ],
    []
  );

  const handleEditClick = () => {
    setNewName(deviceData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setDeviceData({ ...deviceData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  if (loading) {
    return (
      <div className="tw-details-loading">
        <div className="spinner"></div>
        <p>Loading device details...</p>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className="tw-details-error">
        <p>Failed to load device data</p>
        <button onClick={() => navigate(-1)} className="tw-details-back-btn">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="tw-details-container">
      {/* Header */}
      <div className="tw-details-header">
        <button onClick={() => navigate(-1)} className="tw-details-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="tw-details-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 className="tw-details-title">
              {deviceData.name || `Treated Water Device ${deviceId}`}
            </h1>
            <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
              <FiEdit2 size={18} />
            </IotmateButton>
          </div>
          <div className="tw-details-status">
            {deviceData.status === "Online" ? (
              <FiCheckCircle size={24} color="#8fce00" />
            ) : (
              <FiXCircle size={24} color="#ff6b6b" />
            )}
            <span
              style={{
                color: deviceData.status === "Online" ? "#8fce00" : "#ff6b6b",
              }}
            >
              {deviceData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="tw-details-last-update">
          Last updated: {deviceData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Parameters Grid */}
      <div className="tw-details-grid">
        {treatedWaterParameters.map((param, index) => (
          <WaterQualityCard
            key={index}
            param={{ name: param.name, icon: param.icon }}
            value={deviceData[param.key] || 0}
            unit={param.unit}
            status="Good"
          />
        ))}
      </div>

      {/* Historical Data Table */}
      <div className="tw-details-history">
        <DataHistoryTable
          data={historicalData}
          columns={historyColumns}
          title={`${deviceData.name || 'Device'} - Historical Data`}
          dateField="timestamp"
          enableDateFilter={true}
          enableDownload={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Device Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter device name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default TreatedWaterDetails;
